import { Badge, Switch } from "@/components/ui";
import { capitalize, cn, status } from "@/lib/utils";
import { useAppDispatch } from "@/redux/hooks";
import { openSnackbarAction } from "@/redux/slices";
import { routes } from "@/routes/utils";
import useEmployeesQuery from "@/services/employeeManagement/useEmployeeManagementMutation";
import useToogleEmployeeStatussMutation from "@/services/employeeManagement/useToggleEmployeeStatusMutation";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const useEmployeeMgmt = () => {
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const queryClient = useQueryClient();

  const queryParams = {
    ...filterData,
    ...(filterData?.name ? { name: filterData?.name } : {}),
    orderBy: "createdAt",
    orderDirection: "DESC",
    page: pageNumber + 1,
    limit: pageSize,
  };

  const {
    data: response,
    isPending,
    isError,
    error,
  } = useEmployeesQuery(queryParams, {
    onError: (err) => {
      console.error("Error fetching employees:", err);
      toast.error(err?.message || "Error fetching employees");
    },
  });

  const { mutate: toggleEmployeeStatus } = useToogleEmployeeStatussMutation({
    onSuccess: () => {
      toast.success("Employee Status Updated Successfully");
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
    onError: () => {
      toast.error("Failed to update status");
    },
  });

  const tableData = response?.data?.[0]?.records || [];
  const totalRecords = response?.data?.[0]?.pagination?.[0]?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "name",
      header: <div>Name</div>,
      cell: (row: any) => (
        <span
          className="text-primary font-normal cursor-pointer"
          onClick={() => onRowClick(row.row.original)}
        >
          {String(row?.row?.original?.name || "A")}
        </span>
      ),
      size: 80,
    },
    {
      accessorKey: "email",
      header: <div>Email ID</div>,
      accessorFn: (row: any) => String(row?.email || ""),
      cell: ({ row }: any) => {
        const email = row.original.email || "";
        const displayEmail =
          email.length > 20 ? email.substring(0, 20) + "..." : email;

        return <span title={email}>{displayEmail}</span>;
      },
    },

    {
      accessorKey: "mobileNo",
      header: <div>Mobile No.</div>,
      accessorFn: (row: any) => String(row?.mobile_number || ""),
    },
    {
      accessorKey: "role",
      header: <div>Role</div>,
      accessorFn: (row: any) => String(row?.role || ""),
    },
    {
      accessorKey: "geographicalRegion",
      header: <div>Geographical Region</div>,
      size: 20,
      accessorFn: (row: any) => String(row?.geographical_region_name || ""),
    },
    {
      accessorKey: "active",
      header: <div>Status</div>,
      cell: (row: any) => {
        const isActive = row.row.original.status;
        const _status = isActive ? "active" : "inactive";
        return (
          <Badge
            variant="outline"
            className={cn(
              (status as any)[_status]?.color,
              (status as any)[_status]?.bgColor,
              "rounded-[5px] px-4 py-2 border-none"
            )}
          >
            {capitalize(isActive ? "Active" : "Inactive")}
          </Badge>
        );
      },
    },
    {
      accessorKey: "action",
      header: <div>Action</div>,
      cell: (row: any) => (
        <Switch
          checked={row.row.original.status}
          className="cursor-pointer"
          onCheckedChange={() =>
            toggleEmployeeStatus({ id: row.row.original.profile_id })
          }
        />
      ),
    },
  ];

  const onAdd = () => {
    navigate(`${routes.employee}/${routes.add}`);
  };

  const onRowClick = (employee: any) => {
    if (employee?.profile_id) {
      navigate(`${employee.profile_id}/${routes.edit}`);
    } else {
      console.error("No employee ID found:", employee);
    }
  };

  const onCancelFilter = () => {
    setOpenFilter(false);
  };

  const onFilterBtnClick = () => {
    setOpenFilter(true);
  };

  return {
    columns,
    onAdd,
    onCancelFilter,
    onFilterBtnClick,
    openFilter,
    filterData,
    setFilterData,
    tableData,
    onRowClick,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    setPageNumber,
    setPageSize,
  };
};

export default useEmployeeMgmt;
